import { createCommandBus, type CommandBus } from '../core/command-bus.js';
import { createEventBus, type EventBus, type HrEventMap } from '../core/event-bus.js';
import { resolveConfig, type AppConfig, type RawEnv } from '../core/config.js';
import { messagesFor, type Messages } from '../i18n/strings.js';
import { systemClock, type Clock } from '../ports/clock.js';
import { createUlidProvider, type IdProvider } from '../ports/id.js';
import type { Telemetry } from '../ports/telemetry.js';
import { createConsoleTelemetry } from '../adapters/console-telemetry.js';
import type { Principal } from '../ports/principal.js';
import type { EmployeeRepository } from '../ports/employee-repository.js';
import { createMockEmployeeRepository, VENTURES, type VentureInfo } from '../adapters/mock-employees.js';
import type { CaseRepository } from '../ports/case-repository.js';
import { createMemoryCaseRepository } from '../adapters/memory-case-repository.js';
import { createMockSopAgent } from '../adapters/mock-sop-agent.js';
import { createCaseRunner, type CaseRunner } from '../core/case-runner.js';
import { probationCheck } from '../core/policy/probation.js';
import { leaveRequestCheck } from '../core/policy/leave-check.js';
import { annualLeaveEntitlement } from '../core/policy/annual-leave.js';
import { leaveKey, serviceYearsAt, type LeaveTaken } from '../core/leave.js';
import { PLAYBOOKS } from '../core/playbooks/index.js';
import { HR_EVENTS } from '@squadlab/contracts';
import { createStore, type Store } from '../core/store.js';
import type { AppError } from '../core/result.js';
import type { Playbook, VentureId } from '@squadlab/contracts';

/** Session state — which venture's HR data is on screen. */
export interface SessionState {
  readonly ventureId: VentureId;
}

/** Last runner failure, surfaced visibly in the Cases UI (no silent failures). */
export interface RunnerActivity {
  readonly lastError: AppError | null;
}

/**
 * The Container is the single composition root (late binding, ADR 0004).
 *
 * WHY: every module depends on interfaces (ports), never concretes. This is the
 * ONE place adapters are chosen — mock-always in v1 (the People Engine is a
 * simulation; the SIMULATION badge reflects `config.simulation`). Swapping a
 * mock for a live adapter later changes only this file. The clock and id
 * provider are injected here so the rest of the app — and tests — never reach
 * for `Date.now()` / `Math.random()`.
 *
 * Ports arrive per phase: P3 wires `EmployeeRepository`, P4 `CaseRepository` +
 * `SopAgent`, P5 the leave policy checks — all resolved HERE.
 */
export interface Container {
  readonly config: AppConfig;
  readonly messages: Messages;
  readonly clock: Clock;
  readonly id: IdProvider;
  readonly telemetry: Telemetry;
  readonly principal: Principal;
  readonly commands: CommandBus;
  readonly events: EventBus<HrEventMap>;
  /** Registry read seam (mock in v1 — ADR 0004). */
  readonly employees: EmployeeRepository;
  /** Ventures the operator can switch between (seeded in v1). */
  readonly ventures: readonly VentureInfo[];
  /** Live session state (active venture); views subscribe and re-render. */
  readonly session: Store<SessionState>;
  /** Case store (in-memory mock in v1 — ADR 0004). */
  readonly cases: CaseRepository;
  /** The generic SOP case runner (ADR 0052). */
  readonly runner: CaseRunner;
  /** Playbooks the operator can start in v1. */
  readonly playbooks: readonly Playbook[];
  /** Runner failures for the UI to surface visibly. */
  readonly runnerActivity: Store<RunnerActivity>;
  /** Taken leave days per `${ventureId}/${employeeId}` (in-memory in v1). */
  readonly leaveTaken: Store<LeaveTaken>;
  /** Watchdog dedupe: compliance watch key → the renewal case it opened. */
  readonly renewalsOpened: Store<Readonly<Record<string, string>>>;
}

export function createContainer(env: RawEnv, overrides: Partial<Container> = {}): Container {
  const config = overrides.config ?? resolveConfig(env);
  const messages = overrides.messages ?? messagesFor(config.locale);
  const clock = overrides.clock ?? systemClock;
  const id = overrides.id ?? createUlidProvider(clock);
  const telemetry = overrides.telemetry ?? createConsoleTelemetry();
  // Single-operator stub scoped to the core venture; real identity + venture
  // switching arrive with the registry slice (P3).
  const principal = overrides.principal ?? { ownerId: 'operator-local', ventureId: 'squadlab' };
  const commands = overrides.commands ?? createCommandBus();
  const events =
    overrides.events ??
    createEventBus<HrEventMap>({
      onHandlerError: (cause) =>
        telemetry.error({ kind: 'unknown', message: 'Event handler threw', cause }),
    });

  const employees = overrides.employees ?? createMockEmployeeRepository();
  const ventures = overrides.ventures ?? VENTURES;
  const session = overrides.session ?? createStore<SessionState>({ ventureId: principal.ventureId });
  const cases = overrides.cases ?? createMemoryCaseRepository();
  const playbooks = overrides.playbooks ?? PLAYBOOKS;
  const leaveTaken = overrides.leaveTaken ?? createStore<LeaveTaken>({});
  const runner =
    overrides.runner ??
    createCaseRunner({
      cases,
      agent: createMockSopAgent(),
      clock,
      id,
      events,
      resolveEmployee: async (ventureId, employeeId, signal) => {
        const found = await employees.get(ventureId, employeeId, signal);
        return found.ok ? found.value : undefined;
      },
      policyChecks: {
        'onboarding/check-probation': ({ employee }) =>
          employee
            ? probationCheck(employee.contract)
            : { allowed: false, ruleRef: 'n/a', explanation: 'No subject employee on the case.' },
        'leave-request/check-entitlement': ({ employee, input }) => {
          if (!employee || !input?.['startDate'] || !input?.['endDate']) {
            return { allowed: false, ruleRef: 'n/a', explanation: 'Missing the employee or the requested dates.' };
          }
          try {
            return leaveRequestCheck({
              serviceYears: serviceYearsAt(employee.contract.startDate, clock.now()),
              takenDays: leaveTaken.getState()[leaveKey(employee.ventureId, employee.id)] ?? 0,
              startDate: input['startDate'],
              endDate: input['endDate'],
            });
          } catch (cause) {
            return {
              allowed: false,
              ruleRef: 'n/a',
              explanation: cause instanceof Error ? cause.message : 'Invalid leave request dates.',
            };
          }
        },
      },
      recordUpdates: {
        'leave-request/record-balance': ({ hrCase, employee }) => {
          const costed = [...hrCase.steps].reverse().find((s) => s.kind === 'policy-check' && s.verdict?.computed);
          const days = costed?.verdict?.computed?.['requestedWorkingDays'];
          if (!days || !hrCase.employeeId || !employee) {
            throw new Error('No costed leave request (or subject employee) on the case trail');
          }
          const key = leaveKey(hrCase.ventureId, hrCase.employeeId);
          const taken = (leaveTaken.getState()[key] ?? 0) + days;
          leaveTaken.update((prev) => ({ ...prev, [key]: taken }));
          const entitlement = annualLeaveEntitlement(serviceYearsAt(employee.contract.startDate, clock.now()));
          events.publish(HR_EVENTS.leaveBalanceUpdated, {
            correlationId: id.next(),
            occurredAt: clock.now(),
            employeeId: hrCase.employeeId,
            ventureId: hrCase.ventureId,
            balanceDays: Math.max(0, entitlement.days - taken),
          });
        },
      },
    });
  const runnerActivity = overrides.runnerActivity ?? createStore<RunnerActivity>({ lastError: null });
  const renewalsOpened = overrides.renewalsOpened ?? createStore<Readonly<Record<string, string>>>({});

  return {
    config,
    messages,
    clock,
    id,
    telemetry,
    principal,
    commands,
    events,
    employees,
    ventures,
    session,
    cases,
    runner,
    playbooks,
    runnerActivity,
    leaveTaken,
    renewalsOpened,
  };
}
